import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { siteInfo, products } from '../data/mock';
import './Simulateur.css';

// Tracking helper (PostHog déjà chargé dans index.html)
const track = (event, props = {}) => {
  try {
    if (typeof window !== 'undefined' && window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture(event, props);
    }
  } catch (_) { /* no-op */ }
};

// Configuration des véhicules pour le simulateur — les prix kit viennent de mock.js (prix avec pose)
const buildVehicles = () => {
  const findKit = (code) => products.find(p => p.code === code)?.priceWithInstall || 0;
  return [
    { n: 'Moto / Scooter',    r: 'BVA1', icon: '🏍️', label: 'Moto',       kit: findKit('BVA1'), dKm: 8000,  dC: 3.5,  dP: 2.00, isL: true  },
    { n: 'Voiture <2000 cm³', r: 'BVA2', icon: '🚗',  label: 'Voiture',    kit: findKit('BVA2'), dKm: 15000, dC: 7.5,  dP: 2.00, isL: true  },
    { n: 'Voiture >2000 / 4x4 / Camping-car', r: 'BVA3', icon: '🚙',  label: 'Voiture+ / 4x4',  kit: findKit('BVA3'), dKm: 20000, dC: 10.5, dP: 2.10, isL: true  },
    { n: 'Camion / Chantier / Maritime', r: 'BVA4', icon: '🚛', label: 'Camion / Chantier', kit: findKit('BVA4'), dKm: 30000, dC: 12.0, dP: 2.10, isL: true  },
    { n: 'Chauffage mazout / Pellet',    r: 'BVA5', icon: '🔥', label: 'Chauffage',         kit: findKit('BVA5'), dKm: 12,    dC: 200,  dP: 1.20, isL: false },
    { n: 'Industriel',        r: 'BVA6', icon: '🏭',  label: 'Industriel', kit: findKit('BVA6'), dKm: 12,    dC: 2000, dP: 1.20, isL: false },
  ];
};

// Format suisse
const fmtCH = (n) => Math.round(n).toLocaleString('fr-CH');

// Numéro WhatsApp depuis siteInfo (retire +, espaces et caractères non numériques)
const buildWhatsAppNumber = () => {
  const raw = siteInfo.phone || '';
  return raw.replace(/[^0-9]/g, '');
};

const Simulateur = () => {
  const VEHICLES = useMemo(() => buildVehicles(), []);
  const [curV, setCurV] = useState(0);
  const [pct, setPct] = useState(10);
  const [km, setKm] = useState(VEHICLES[0].dKm);
  const [conso, setConso] = useState(VEHICLES[0].dC);
  const [prix, setPrix] = useState(VEHICLES[0].dP);
  const [fleet, setFleet] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(false);
  const pdfRef = useRef(null);

  const v = VEHICLES[curV];

  // Quand on change de véhicule, on remet les valeurs par défaut
  useEffect(() => {
    setKm(v.dKm);
    setConso(v.dC);
    setPrix(v.dP);
  }, [curV, v.dKm, v.dC, v.dP]);

  // Calculs
  const pctF  = (parseInt(pct, 10) || 0) / 100;
  const kmN   = parseFloat(km)    || 0;
  const consoN= parseFloat(conso) || 0;
  const prixN = parseFloat(prix)  || 0;
  const fleetN= parseInt(fleet, 10) || 1;

  const ecoU  = v.isL ? (consoN / 100 * kmN * prixN * pctF) : (consoN * kmN * prixN * pctF);
  const eco   = Math.round(ecoU * fleetN);
  const invest= v.kit * fleetN;
  const ecoM  = eco > 0 ? Math.round(eco / 12) : 0;
  const roiM  = eco > 0 ? (invest / eco * 12) : 0;
  const g5    = Math.round(eco * 5 - invest);

  let roiTxt = '—', roiU = '—', badge = '—';
  if (eco > 0) {
    if (roiM < 4)        { roiTxt = `${Math.round(roiM)}`;             roiU = 'mois'; badge = `⚡ Remboursé en ${Math.round(roiM)} mois`; }
    else if (roiM < 12)  { roiTxt = `${Math.round(roiM)}`;             roiU = 'mois'; badge = `✅ Remboursé en ${Math.round(roiM)} mois`; }
    else if (roiM < 30)  { roiTxt = (roiM/12).toFixed(1).replace('.', ','); roiU = 'ans'; badge = `✅ Remboursé en ${(roiM/12).toFixed(1).replace('.', ',')} ans`; }
    else                 { roiTxt = (roiM/12).toFixed(1).replace('.', ','); roiU = 'ans'; badge = `📅 Retour en ${(roiM/12).toFixed(1).replace('.', ',')} ans`; }
  }

  // WhatsApp pre-fill
  const waNumber = buildWhatsAppNumber();
  const waText = encodeURIComponent(
    `Bonjour, j'ai simulé mes économies avec le kit Ecopra ${v.r} :\n` +
    `- Économie estimée : ${fmtCH(eco)} CHF/an\n` +
    `- Remboursement en ${roiTxt} ${roiU}\n` +
    `Je souhaite un devis.`
  );
  const waHref = `https://wa.me/${waNumber}?text=${waText}`;

  // Track WhatsApp click
  const handleWhatsAppClick = () => {
    track('simulateur_whatsapp_click', {
      vehicle_code: v.r,
      vehicle_name: v.n,
      annual_savings_chf: eco,
      kit_price_chf: invest,
      roi_months: Math.round(roiM),
      economy_pct: Math.round(pctF * 100),
      fleet_size: fleetN,
    });
  };

  // Track simulation results (debounced to fire once when valid)
  useEffect(() => {
    if (eco > 0) {
      track('simulateur_calculated', {
        vehicle_code: v.r,
        annual_savings_chf: eco,
        roi_months: Math.round(roiM),
        economy_pct: Math.round(pctF * 100),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.r, eco]);

  // PDF export
  const handleDownloadPdf = async () => {
    if (pdfLoading || !pdfRef.current) return;
    setPdfLoading(true);
    track('simulateur_pdf_download', {
      vehicle_code: v.r,
      annual_savings_chf: eco,
      roi_months: Math.round(roiM),
    });
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const filename = `Ecopra_Simulation_${v.r}_${fmtCH(eco)}CHF.pdf`;
      const opt = {
        margin: [10, 10, 10, 10],
        filename,
        image: { type: 'jpeg', quality: 0.96 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      };
      await html2pdf().set(opt).from(pdfRef.current).save();
    } catch (err) {
      // silent fallback
      console.error('PDF generation failed', err);
    } finally {
      setPdfLoading(false);
    }
  };

  // Slider gradient
  const sliderMin = 5, sliderMax = 35;
  const sliderPos = ((pct - sliderMin) / (sliderMax - sliderMin) * 100).toFixed(1) + '%';

  const lblKm    = v.isL ? 'Km par an'        : 'Mois / an';
  const lblConso = v.isL ? 'Litres / 100 km'  : 'Litres / mois';

  return (
    <div className="ecopra-simulateur" data-testid="simulateur-page">
      {/* HERO */}
      <section className="es-hero">
        <h1>
          Combien pouvez-vous<br />
          <em>économiser</em> avec le BVA ?
        </h1>
        <p>Simulez vos économies de carburant en 30 secondes — sans engagement.</p>
        <div className="es-badges">
          <span className="es-badge">🌿 Sans modification moteur</span>
          <span className="es-badge">⏱ Installation 50 min</span>
          <span className="es-badge">🇨🇭 Installateur Suisse</span>
        </div>
      </section>

      {/* MAIN */}
      <div className="es-main">
        <div className="es-card" ref={pdfRef}>

          {/* VEHICLE TABS */}
          <div className="es-vtabs" data-testid="simulateur-vehicle-tabs">
            {VEHICLES.map((veh, i) => (
              <button
                key={veh.r}
                type="button"
                data-testid={`simulateur-veh-${veh.r}`}
                className={`es-vt ${curV === i ? 'on' : ''}`}
                onClick={() => setCurV(i)}
              >
                <div className="es-vt-icon">{veh.icon}</div>
                <div className="es-vt-name">{veh.label}</div>
                <div className="es-vt-ref">{veh.r}</div>
              </button>
            ))}
          </div>

          {/* INPUTS */}
          <div className="es-inputs">
            <div className="es-fl">
              <label htmlFor="sim-km">{lblKm}</label>
              <input
                id="sim-km"
                data-testid="simulateur-input-km"
                type="number"
                min="0"
                value={km}
                onChange={(e) => setKm(e.target.value)}
              />
            </div>
            <div className="es-fl">
              <label htmlFor="sim-conso">{lblConso}</label>
              <input
                id="sim-conso"
                data-testid="simulateur-input-conso"
                type="number"
                step="0.1"
                min="0"
                value={conso}
                onChange={(e) => setConso(e.target.value)}
              />
            </div>
            <div className="es-fl">
              <label htmlFor="sim-prix">Prix carburant (CHF / L)</label>
              <input
                id="sim-prix"
                data-testid="simulateur-input-prix"
                type="number"
                step="0.01"
                min="0"
                value={prix}
                onChange={(e) => setPrix(e.target.value)}
              />
            </div>
            <div className="es-fl">
              <label htmlFor="sim-fleet">Nb de véhicules</label>
              <input
                id="sim-fleet"
                data-testid="simulateur-input-fleet"
                type="number"
                step="1"
                min="1"
                max="100"
                value={fleet}
                onChange={(e) => setFleet(e.target.value)}
              />
            </div>
          </div>

          {/* SLIDER */}
          <div className="es-slider-section">
            <div className="es-slider-header">
              <div className="es-slider-label">Économie estimée avec le kit BVA</div>
              <div className="es-slider-pct" data-testid="simulateur-pct">{pct}%</div>
            </div>
            <input
              type="range"
              min={sliderMin}
              max={sliderMax}
              step="1"
              value={pct}
              data-testid="simulateur-slider"
              onChange={(e) => setPct(parseInt(e.target.value, 10))}
              style={{ '--pct': sliderPos }}
            />
            <div className="es-slider-tips">
              <span>Usage normal · 5%</span>
              <span>Optimisé · 20%</span>
              <span>Idéal · 35%</span>
            </div>
          </div>

          {/* RESULTS */}
          <div className="es-result-wrap">
            <div className="es-result-hero es-anim">
              <div className="es-result-lbl">
                {fleetN > 1 ? `Économie pour ${fleetN} véhicules / an` : 'Économie annuelle estimée'}
              </div>
              <div className="es-result-big">
                <span data-testid="simulateur-result-eco">{fmtCH(eco)}</span>
                <span className="es-result-unit"> CHF</span>
              </div>
              <div className="es-result-sub">
                {Math.round(pctF * 100)}% d'économie · {v.n}
              </div>
              <div className="es-result-badge" data-testid="simulateur-result-badge">{badge}</div>
            </div>

            <div className="es-mini-stats es-anim">
              <div className="es-ms-card">
                <div className="es-ms-lbl">Économie / mois</div>
                <div className="es-ms-val" data-testid="simulateur-result-mois">{eco > 0 ? fmtCH(ecoM) : '—'}</div>
                <div className="es-ms-sub">CHF</div>
              </div>
              <div className="es-ms-card">
                <div className="es-ms-lbl">Kit + installation</div>
                <div className="es-ms-val" data-testid="simulateur-result-invest">{fmtCH(invest)}</div>
                <div className="es-ms-sub">CHF tout compris</div>
              </div>
              <div className="es-ms-card">
                <div className="es-ms-lbl">Remboursé en</div>
                <div className="es-ms-val" data-testid="simulateur-result-roi">{roiTxt}</div>
                <div className="es-ms-sub">{roiU}</div>
              </div>
              <div className="es-ms-card">
                <div className="es-ms-lbl">Gain net sur 5 ans</div>
                <div className="es-ms-val" data-testid="simulateur-result-5ans">{g5 > 0 ? fmtCH(g5) : '—'}</div>
                <div className="es-ms-sub">CHF</div>
              </div>
            </div>

            <div className="es-cta es-anim">
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleWhatsAppClick}
                className="es-btn es-btn-wa"
                data-testid="simulateur-btn-whatsapp"
              >
                📱 Envoyer ma simulation sur WhatsApp
              </a>
              <button
                type="button"
                onClick={handleDownloadPdf}
                disabled={pdfLoading || eco <= 0}
                className="es-btn es-btn-secondary"
                data-testid="simulateur-btn-pdf"
              >
                {pdfLoading ? '⏳ Génération du PDF...' : '📄 Télécharger ma simulation en PDF'}
              </button>
              <Link
                to="/commander"
                onClick={() => track('simulateur_order_click', { vehicle_code: v.r, kit_price_chf: invest })}
                className="es-btn es-btn-secondary"
                data-testid="simulateur-btn-commander"
              >
                Commander mon kit {v.r}
              </Link>
            </div>
          </div>

          {/* TRUST */}
          <div className="es-trust">
            <div className="es-trust-item">✓ Sans modification moteur</div>
            <div className="es-trust-item">✓ Garantie constructeur préservée</div>
            <div className="es-trust-item">✓ Installation en 50 min</div>
          </div>

          {/* FOOTER NOTE */}
          <div className="es-foot">
            Simulation indicative basée sur vos paramètres de consommation. Les résultats réels
            peuvent varier selon le véhicule et les conditions d'utilisation.
            Prix net en CHF — TVA non applicable, entreprise non assujettie TVA. Ecopra BVA — Suisse.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Simulateur;
